/**
 * RDF Graph — RDF graph client.
 *
 * Five endpoints under ``/runs/{id}/rdf/``:
 *
 *   POST /build              → triples_count + manuscripts_count
 *   GET  /graph              → Cytoscape JSON (nodes + edges + truncation flag)
 *   GET  /download.ttl       → static URL (downloadUrl helper)
 *   POST /validate           → SHACL report
 *   GET  /status             → idle | built | validated | error
 *
 * The graph response is Cytoscape-shaped already; the route component
 * passes it straight to ``react-cytoscapejs``.
 */

import { api } from "@/api/client";


export interface GraphNode {
  id: string;
  label: string;
  type: string;
  color: string;
  properties?: Record<string, string[]>;
  /** Server-computed position. Cytoscape uses ``preset`` layout to
   *  pin these so we don't pay for a browser-side layout pass. */
  position?: { x: number; y: number };
}


export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  predicate: string;
  predicate_label?: string;
}


export interface GraphResponse {
  nodes: GraphNode[];
  edges: GraphEdge[];
  truncated: boolean;
  total_nodes: number;
  total_edges: number;
  layout?: string;
  manuscript_count?: number | null;
  manuscripts_in_view?: number | null;
}


export interface GraphCatalogResponse {
  total_nodes: number;
  total_edges: number;
  node_types: Record<string, number>;
  edge_predicates: Record<string, number>;
  manuscript_count: number;
  f4_singleton_count?: number | null;
}

export type CanvasBudget = 500 | 1000 | 2000;


export interface ViewportParams {
  types?: string[];
  predicates?: string[];
  q?: string;
  seed?: string;
  radius?: number;
  maxNodes?: number;
  layout?: ServerLayout;
  manuscriptsOnly?: boolean;
}

// Layouts the BACKEND can compute. The browser no longer runs layouts.
export type ServerLayout =
  | "spring"
  | "kamada_kawai"
  | "circular"
  | "shell"
  | "concentric";


// One RDF type attached to a node (rdf:type triple).
export interface NodeTypeRef {
  uri:   string;
  label: string;
}

// Datatype property: literal value with a predicate.
export interface NodeProperty {
  predicate:       string;
  predicate_label: string;
  value:           string;
  datatype?:       string | null;
}

// One incoming or outgoing edge to/from another node.
export interface NodeEdgeRef {
  predicate:       string;
  predicate_label: string;
  target_id?:      string;     // outgoing direction
  target_label?:   string;
  target_type?:    string;
  target_color?:   string;
  source_id?:      string;     // incoming direction
  source_label?:   string;
  source_type?:    string;
  source_color?:   string;
}

export interface NodeDetail {
  id:         string;
  label:      string;
  type:       string;
  color:      string;
  types:      NodeTypeRef[];
  properties: NodeProperty[];
  outgoing:   NodeEdgeRef[];
  incoming:   NodeEdgeRef[];
}


// One usage example for an ontology class ("typed" node) or property
// (predicate) found in a run's actual RDF graph.
export interface OntologyUsageExample {
  node_id?:          string | null;
  label?:            string | null;
  category?:         string | null;
  subject_id?:       string | null;
  subject_label?:    string | null;
  object_id?:        string | null;
  object_label?:     string | null;
  object_is_literal?: boolean | null;
}

export interface OntologyUsageResponse {
  built:        boolean;
  entity_kind:  "class" | "property" | string;
  count:        number;
  examples:     OntologyUsageExample[];
  total_triples: number;
}


export interface ShaclViolation {
  focus_node: string;
  source_shape: string;
  severity: string;        // "Violation" | "Warning" | "Info"
  message: string;
  value: string | null;
}


export interface ShaclReport {
  conforms: boolean;
  violations: ShaclViolation[];
}


export interface RdfBuildResponse {
  triples_count: number;
  manuscripts_count: number;
  output_path: string;
  started_at: string;
  finished_at: string;
  mapping_errors: string[];
  coverage_path?: string | null;
  unknown_class_count?: number | null;
}


export interface RdfBuildOptions {
  add_epistemological_status?: boolean;
  add_cataloging_view?: boolean;
  add_philological_overlay?: boolean;
}


export interface RdfCoverageClass {
  class_uri: string;
  class_local_name: string;
  hmo_node_count: number;
  projection_status: string;
  wikidata_representation?: string;
  notes?: string;
}


export interface RdfOntologyCoverageResponse {
  classes_covered: number;
  classes_total: number;
  properties_covered: number;
  properties_total: number;
  missing_classes: string[];
  missing_properties: string[];
  class_percent: number;
  property_percent: number;
}


export interface RdfCoverageResponse {
  rdf_class_count: number;
  unknown_class_count: number;
  classes: RdfCoverageClass[];
}


export interface RdfStatus {
  status: "idle" | "built" | "validated" | "error" | string;
  triples_count?: number | null;
  manuscripts_count?: number | null;
  last_built_at?: string | null;
  error?: string | null;
}


export interface TripleOverrideRequest {
  subject_uri: string;
  predicate_uri: string;
  new_value: string;
  new_datatype?: string;
  new_lang?: string;
}

export interface TripleOverrideResponse {
  id: string;
  subject_uri: string;
  predicate_uri: string;
  new_value: string;
  new_datatype?: string;
  new_lang?: string;
  old_value?: string;
  created_at: string;
}

export async function saveTripleOverride(
  runId: string,
  body: TripleOverrideRequest,
): Promise<TripleOverrideResponse> {
  const res = await fetch(`/api/runs/${runId}/rdf/triple`, {
    method: "PATCH",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<TripleOverrideResponse>;
}

export async function listTripleOverrides(runId: string): Promise<TripleOverrideResponse[]> {
  const res = await fetch(`/api/runs/${runId}/rdf/overrides`);
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<TripleOverrideResponse[]>;
}


export const Rdf = {
  build: (runId: string, options?: RdfBuildOptions) =>
    api.post<RdfBuildResponse>(`/runs/${runId}/rdf/build`, options ?? {}),

  coverage: (runId: string) =>
    api.get<RdfCoverageResponse>(`/runs/${runId}/rdf/coverage`),

  ontologyCoverage: (runId: string) =>
    api.get<RdfOntologyCoverageResponse>(`/runs/${runId}/rdf/ontology-coverage`),

  graph: (runId: string, maxNodes = 500, layout: ServerLayout = "spring") =>
    api.get<GraphResponse>(
      `/runs/${runId}/rdf/graph?max_nodes=${maxNodes}&layout=${layout}`,
    ),

  catalog: (runId: string) =>
    api.get<GraphCatalogResponse>(`/runs/${runId}/rdf/catalog`),

  viewport: (runId: string, params: ViewportParams = {}) => {
    const sp = new URLSearchParams();
    const maxNodes = params.maxNodes ?? 500;
    const layout = params.layout ?? "spring";
    sp.set("max_nodes", String(maxNodes));
    sp.set("layout", layout);
    if (params.q) sp.set("q", params.q);
    if (params.seed) sp.set("seed", params.seed);
    if (params.radius) sp.set("radius", String(params.radius));
    if (params.manuscriptsOnly) sp.set("manuscripts_only", "true");
    for (const t of params.types ?? []) sp.append("types", t);
    for (const p of params.predicates ?? []) sp.append("predicates", p);
    return api.get<GraphResponse>(`/runs/${runId}/rdf/viewport?${sp.toString()}`);
  },

  ego: (runId: string, center: string, params: Omit<ViewportParams, "seed"> = {}) => {
    const sp = new URLSearchParams();
    sp.set("center", center);
    sp.set("max_nodes", String(params.maxNodes ?? 500));
    sp.set("layout", params.layout ?? "spring");
    sp.set("radius", String(params.radius ?? 2));
    return api.get<GraphResponse>(`/runs/${runId}/rdf/ego?${sp.toString()}`);
  },

  /** Fetch full detail for one node (clicked in the graph view).
   *  ``nodeId`` is passed as a query param so URIs with slashes work
   *  without encoding the whole path. */
  node: (runId: string, nodeId: string) =>
    api.get<NodeDetail>(
      `/runs/${runId}/rdf/node?id=${encodeURIComponent(nodeId)}`,
    ),

  /** Real usage of one HMO ontology class/property in this run's RDF
   *  graph — backs the schema bootstrap detail drawer's "based on the
   *  RDF graph" section. Returns ``built: false`` (not an error) when
   *  the run has no RDF built yet. */
  ontologyUsage: (runId: string, ontologyUri: string, entityKind: "class" | "property") =>
    api.get<OntologyUsageResponse>(
      `/runs/${runId}/rdf/ontology-usage?ontology_uri=${encodeURIComponent(ontologyUri)}` +
      `&entity_kind=${entityKind}`,
    ),

  validate: (runId: string) =>
    api.post<ShaclReport>(`/runs/${runId}/rdf/validate`, {}),

  status: (runId: string) =>
    api.get<RdfStatus>(`/runs/${runId}/rdf/status`),

  downloadUrl: (runId: string) =>
    `/api/runs/${runId}/rdf/download.ttl`,
};
